/**
 * Environment Variable Validation Utility
 * Ensures all required environment variables are present before app initialization
 */

// Define all required environment variables for the application
const requiredEnvVars = [
  "REACT_APP_BACKEND_API_URL",
  "REACT_APP_BACKEND_API_KEY",
  "REACT_APP_SUPABASE_AUTH_URL",
  "REACT_APP_SUPABASE_PUBLISHABLE_AUTH_DEFAULT_KEY",
  "REACT_APP_SUPABASE_BUCKET_URL",
  "REACT_APP_SUPABASE_BUCKET_ANON_KEY",
  "REACT_APP_GOOGLE_MAPS_API",
  "REACT_APP_MAP_API",
] as const;

/**
 * Validates that all required environment variables are present
 * @throws {Error} If any required environment variables are missing
 */
export const validateEnvironment = (): void => {
  const missing: string[] = [];
  const warnings: string[] = [];

  // Check required variables
  requiredEnvVars.forEach((varName) => {
    const value = process.env[varName];
    if (!value || value.trim() === "") {
      missing.push(varName);
    } else if (value.includes("XXX")) {
      warnings.push(`${varName} appears to be a placeholder value`);
    }
  });

  // Log warnings for placeholder values
  if (warnings.length > 0) {
    console.warn("⚠️  Environment Variable Warnings:");
    warnings.forEach((warning) => console.warn(`   ${warning}`));
  }

  // Throw error if any required variables are missing
  if (missing.length > 0) {
    const errorMessage = `
❌ Missing Required Environment Variables:

${missing.map((varName) => `   • ${varName}`).join("\n")}

Please create a .env file in your project root and add these variables.
    `.trim();

    throw new Error(errorMessage);
  }

  // Success message in development
  if (process.env.NODE_ENV === "development") {
    console.log("✅ All required environment variables are present");
  }
};

type FeatureName = "backend-api" | "supabase" | "google-maps" | "maps";

/**
 * Validates environment for specific features
 * @param feature - Feature name to validate
 * @returns Whether the feature can be used
 */
export const validateFeature = (feature: FeatureName): boolean => {
  const featureRequirements: Record<FeatureName, string[]> = {
    "backend-api": ["REACT_APP_BACKEND_API_URL", "REACT_APP_BACKEND_API_KEY"],
    supabase: [
      "REACT_APP_SUPABASE_AUTH_URL",
      "REACT_APP_SUPABASE_PUBLISHABLE_AUTH_DEFAULT_KEY",
      "REACT_APP_SUPABASE_BUCKET_URL",
      "REACT_APP_SUPABASE_BUCKET_ANON_KEY",
    ],
    "google-maps": ["REACT_APP_GOOGLE_MAPS_API"],
    maps: ["REACT_APP_MAP_API"],
  };

  const requirements = featureRequirements[feature];
  if (!requirements) {
    return false;
  }

  return requirements.every((envVar) => {
    const value = process.env[envVar];
    return value && value.trim() !== "" && !value.includes("XXX");
  });
};
